/**
 * Paths that must keep working after this branch lands, mapped to where each page lives now.
 * `routes.ts` turns every entry into a redirect; `cms/lib/route_utils.ts` folds the keys into its
 * route-match list. Both read `LEGACY_PATHS` so neither can drift from the other.
 *
 * A `:param` in a target is filled from the matched route params, so `/orders/123` reaches the order
 * it names rather than the order list. Trailing slashes need no entries of their own: mithril
 * compiles every route pattern with an optional `/` at the end, and the CMS path helper trims one.
 *
 * Safe to drop after 2026-10-01, once mail carrying these links is old enough that nobody is clicking
 * it — provided nothing emits them by then.
 */

/**
 * Portal routes that are live on `main` today and that this branch renames. Every one of these
 * breaks the moment the branch merges — not at some point in the past — which is why the list is
 * taken from `origin/main:routes.ts` rather than from what feels legacy.
 *
 * `/privacy` is deliberately absent: it keeps its path as a CMS page. `/dashboard` too — `server.ts`
 * already answers it with a real 302. Mail that still emits the old forms was corrected alongside
 * this map (`common_lib/lib/email.py`, `common_lib/domain/`, `fact2server/api/portal/order/confirm.py`,
 * `factserver/domain/links.py`); `factserver/domain/offer/queries.py` emits an unfixable hashbang form.
 */
export const LEGACY_PORTAL_PATHS: Record<string, string> = {
    '/favourites': '/shop/favourites',
    // Three of main's seven My Portal tabs lose their path here: profile merged into settings, and
    // privacy/terms became CMS pages. The fourth, `/my-portal/security`, has no successor — see the
    // note at the bottom of this file.
    '/my-portal/privacy': '/privacy',
    '/my-portal/profile': '/my-portal/settings',
    '/my-portal/terms': '/terms-conditions',
    '/offers': '/shop',
    '/orders': '/my-portal/orders',
    '/orders/:artkey': '/my-portal/orders/:artkey',
    '/orders/:artkey/checkout': '/shop/checkout',
    '/registration': '/register',
    '/terms': '/terms-conditions',
    '/user/forgot-password': '/forgot-password',
    '/user/reset-password': '/reset-password',
}

/**
 * Pages of the WordPress site at movingspirits.eu, mapped to the CMS views that replace them. These
 * only start mattering when that domain points at this portal; until then they are inert.
 *
 * Most of that site needs no entry — the CMS slugs `/about-us` and `/our-team` were chosen to match
 * their WordPress URLs exactly, so only the pages whose slug actually changed are listed here.
 */
export const LEGACY_SITE_PATHS: Record<string, string> = {
    '/logistics-partners': '/our-logistics-partners',
    // `/our-brands` did match WordPress, and was the CMS slug too until the page was rebranded
    // "Our Range" — so this one entry catches both the WordPress URL and any link already out there
    // under the former CMS path.
    '/our-brands': '/our-range',
    '/privacy-statement-eu': '/privacy',
    '/terms-and-conditions': '/terms-conditions',
}

/** Every legacy path, in the order `routes.ts` registers them. */
export const LEGACY_PATHS: Record<string, string> = {...LEGACY_PORTAL_PATHS, ...LEGACY_SITE_PATHS}

/**
 * The query of a legacy link, in the vocabulary the shop filters use now: Specials moved off the
 * `offertype` filter onto `offer` when the special-offer surfaces were merged (#2205), and only the
 * `offer` form still has an option to match it. On any other legacy path the rewrite is a no-op.
 *
 * Only that one pair is touched; everything else is passed through as-is rather than reserialized,
 * because mithril's query parser decodes with `decodeURIComponent` alone — a `URLSearchParams`
 * round-trip would turn the space in `search=Jack Daniels` into a `+` that then survives into the
 * search term.
 */
export function legacy_portal_query(search: string): string {
    const pairs = (search.startsWith('?') ? search.slice(1) : search).split('&').filter(Boolean)
    if (pairs.length === 0) {
        return ''
    }
    return `?${pairs.map((pair) => (pair === 'offertype=SPECIALS' ? 'offer=SPECIALS' : pair)).join('&')}`
}

/*
 * `/my-portal/security` is knowingly absent. On main it is the change-password form (current / new /
 * repeat, posting to the portal user endpoint); this branch has neither the page nor a password field
 * anywhere in settings. Pointing it at `/my-portal/settings` would turn a missing feature into a
 * redirect that quietly lands on a page which cannot do the thing the link promised, so it stays
 * unmapped until the form has somewhere to live.
 */

/** Fill the `:param` segments of a `LEGACY_PATHS` target from the matched route's params. */
export function legacy_portal_target(target: string, params: Record<string, string>): string {
    return target.replace(/:(\w+)/gu, (_match, name: string) => params[name] ?? '')
}
